<template>
  <nav ref="navRef" :class="{ scrolled: isScrolled }">
    <div class="nav-container">
      <div class="logo">
        <img src="@/assets/logo.svg" alt="logo" class="logo-img" />
        <span class="logo-text">宝能卓越</span>
        <!-- <span class="logo-accent">iSmartSys</span> -->
      </div>

      <div class="nav-links">
        <a href="/" :class="{ active: isActive('/') }">首页</a>
        <a href="/solution.html" :class="{ active: isActive('/solution.html') }"
          >解决方案</a
        >
        <a href="/product.html" :class="{ active: isActive('/product.html') }"
          >产品介绍</a
        >
        <a href="/project.html" :class="{ active: isActive('/project.html') }"
          >用户案例</a
        >
        <a href="/news.html" :class="{ active: isActive('/news.html') }"
          >最新资讯</a
        >
        <a href="/about.html" :class="{ active: isActive('/about.html') }"
          >关于我们</a
        >
      </div>

      <WeatherCalendar class="weather-calendar-wrapper" />

      <button class="theme-toggle" @click="toggleTheme" :title="themeTitle">
        <div class="theme-icon-wrapper">
          <svg
            v-if="currentTheme === 'dark'"
            class="theme-icon sun-icon"
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <circle cx="12" cy="12" r="5" />
            <line x1="12" y1="1" x2="12" y2="3" />
            <line x1="12" y1="21" x2="12" y2="23" />
            <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
            <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
            <line x1="1" y1="12" x2="3" y2="12" />
            <line x1="21" y1="12" x2="23" y2="12" />
            <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
            <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
          </svg>
          <svg
            v-else
            class="theme-icon moon-icon"
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
          </svg>
        </div>
        <div class="theme-indicator"></div>
      </button>

      <div class="hamburger" @click="toggleMobileMenu">
        <span></span>
        <span></span>
        <span></span>
      </div>
    </div>

    <div class="mobile-nav" :class="{ show: showMobile }">
      <a href="/" :class="{ active: isActive('/') }" @click="showMobile = false"
        >首页</a
      >
      <a
        href="/solution.html"
        :class="{ active: isActive('/solution.html') }"
        @click="showMobile = false"
        >解决方案</a
      >
      <a
        href="/product.html"
        :class="{ active: isActive('/product.html') }"
        @click="showMobile = false"
        >产品介绍</a
      >
      <a
        href="/project.html"
        :class="{ active: isActive('/project.html') }"
        @click="showMobile = false"
        >用户案例</a
      >
      <a
        href="/news.html"
        :class="{ active: isActive('/news.html') }"
        @click="showMobile = false"
        >最新资讯</a
      >
      <a
        href="/about.html"
        :class="{ active: isActive('/about.html') }"
        @click="showMobile = false"
        >关于我们</a
      >
    </div>
  </nav>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from "vue";
import { useRoute } from "vue-router";
import { useStore } from "vuex";
import WeatherCalendar from "./WeatherCalendar.vue";

const route = useRoute();
const store = useStore();

const navRef = ref(null);
const showMobile = ref(false);
const isScrolled = ref(false);

const currentTheme = computed(() => store.state.theme);

const themeTitle = computed(() => {
  return currentTheme.value === "dark" ? "浅色模式" : "深色模式";
});

const currentPath = computed(() => route.path);
const currentRouteName = computed(() => route.name);

const isActive = (path) => {
  if (path === "/") {
    return currentPath.value === "/" && currentRouteName.value === "home";
  }
  return currentPath.value === path;
};

const toggleMobileMenu = () => {
  showMobile.value = !showMobile.value;
};

const toggleTheme = () => {
  const newTheme = currentTheme.value === "dark" ? "light" : "dark";
  store.dispatch("setTheme", newTheme);
};

const handleResize = () => {
  if (window.innerWidth > 768) {
    showMobile.value = false;
  }
};

const handleScroll = () => {
  isScrolled.value = window.scrollY > 50;
};

onMounted(() => {
  window.addEventListener("resize", handleResize);
  window.addEventListener("scroll", handleScroll);
});

onUnmounted(() => {
  window.removeEventListener("resize", handleResize);
  window.removeEventListener("scroll", handleScroll);
});
</script>

<style lang="scss" scoped>
@import "@/assets/scss/_variables.scss";

nav {
  width: 100%;
  background: rgba(255, 255, 255, 0.75);
  /* stylelint-disable-next-line property-no-vendor-prefix */
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.08);
  position: fixed;
  top: 0;
  left: 0;
  z-index: 9999;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  border-bottom: 1px solid rgba(255, 255, 255, 0.15);

  &.scrolled {
    background: rgba(255, 255, 255, 0.98);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
    border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  }
}

[data-theme="dark"] nav {
  background: rgba(15, 15, 15, 0.7);
  /* stylelint-disable-next-line property-no-vendor-prefix */
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.25);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);

  &.scrolled {
    background: rgba(15, 15, 15, 0.95);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  }

  .logo-text {
    color: rgba(255, 255, 255, 0.95);
  }

  .nav-links a {
    color: rgba(255, 255, 255, 0.85);

    &:hover,
    &.active {
      color: var(--accent-color);
    }
  }

  .hamburger span {
    background: rgba(255, 255, 255, 0.85);
  }
}

.nav-container {
  max-width: 1200px;
  margin: 0 auto;
  height: 76px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 20px;
  gap: 20px;
}

.weather-calendar-wrapper {
  display: block;

  @media (max-width: $breakpoint-tablet) {
    display: none;
  }
}

.logo {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 20px;
  font-weight: bold;
  transition: color 0.3s ease;
}

.logo-text {
  color: var(--text-primary);
  transition: color 0.3s ease;
}

.logo-accent {
  color: var(--accent-color);
}

.logo-img {
  height: 30px;
  width: auto;
}

.nav-links {
  display: flex;
  gap: 40px;
}

.nav-links a {
  position: relative;
  text-decoration: none;
  color: var(--text-primary);
  font-size: 15px;
  font-weight: 450;
  transition: all 0.3s ease;
  padding: 8px 0;

  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    width: 0;
    height: 2px;
    background: var(--accent-color);
    border-radius: 1px;
    transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  &:hover {
    color: var(--accent-color);

    &::after {
      width: 100%;
    }
  }

  &.active {
    color: var(--accent-color);
    font-weight: 550;

    &::after {
      width: 100%;
    }
  }
}

.theme-toggle {
  background: transparent;
  border: none;
  cursor: pointer;
  color: var(--text-primary);
  padding: 10px;
  border-radius: 50%;
  transition: all 0.3s ease;
  margin-right: 10px;
  position: relative;
  overflow: hidden;

  &:hover {
    background: var(--bg-tertiary);
    transform: scale(1.1);
  }

  &:active {
    transform: scale(0.95);
  }

  .theme-icon-wrapper {
    position: relative;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .theme-icon {
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .sun-icon {
    animation: sunPulse 3s ease-in-out infinite;
  }

  .moon-icon {
    animation: moonGlow 4s ease-in-out infinite;
  }

  .theme-indicator {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 0;
    height: 0;
    background: radial-gradient(
      circle,
      rgba(230, 126, 34, 0.3) 0%,
      transparent 70%
    );
    border-radius: 50%;
    transform: translate(-50%, -50%);
    transition: width 0.4s ease, height 0.4s ease;
    z-index: 1;
  }

  &:hover .theme-indicator {
    width: 60px;
    height: 60px;
  }
}

@keyframes sunPulse {
  0%,
  100% {
    transform: scale(1);
    filter: brightness(1);
  }

  50% {
    transform: scale(1.1);
    filter: brightness(1.2);
  }
}

@keyframes moonGlow {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }

  50% {
    opacity: 0.8;
    transform: scale(0.95);
  }
}

.hamburger {
  display: none;
  flex-direction: column;
  gap: 5px;
  cursor: pointer;
}

.hamburger span {
  display: block;
  width: 25px;
  height: 3px;
  background: var(--text-primary);
  border-radius: 2px;
  transition: background-color 0.3s ease;
}

.mobile-nav {
  position: absolute;
  top: 64px;
  left: 0;
  width: 100%;
  background: rgba(255, 255, 255, 0.95);
  /* stylelint-disable-next-line property-no-vendor-prefix */
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
  flex-direction: column;
  padding: 20px 0;
  display: none;
  opacity: 0;
  transform: translateY(-10px);
  transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  border-bottom-left-radius: 20px;
  border-bottom-right-radius: 20px;
}

[data-theme="dark"] .mobile-nav {
  background: rgba(20, 20, 20, 0.95);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.4);
}

.mobile-nav a {
  position: relative;
  padding: 16px 25px;
  color: var(--text-primary);
  text-decoration: none;
  font-size: 15px;
  transition: all 0.3s ease;

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 0;
    height: 40%;
    background: var(--accent-color);
    border-radius: 0 2px 2px 0;
    transition: width 0.3s ease;
  }

  &:hover {
    color: var(--accent-color);
    background: rgba(230, 126, 34, 0.05);
    padding-left: 30px;

    &::before {
      width: 4px;
    }
  }

  &.active {
    color: var(--accent-color);
    font-weight: 500;

    &::before {
      width: 4px;
    }
  }
}

.mobile-nav.show {
  display: flex;
  opacity: 1;
  transform: translateY(0);
}

@media (max-width: $breakpoint-tablet) {
  .logo {
    font-size: 16px !important;
  }

  .nav-links {
    gap: 20px !important;
  }

  .nav-links a {
    font-size: 14px;
  }
}

@media (max-width: $breakpoint-mobile) {
  .nav-links {
    display: none !important;
  }

  .hamburger {
    display: flex !important;
  }

  .theme-toggle {
    display: block;
  }
}
</style>
